import { useEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'
import { Helper } from '@react-three/drei'

// One live spotlight on an orbit rig — a group at world origin yaws about Y;
// the light sits at local [0, height, distance] (distance clamped >= 0) so it
// orbits the Y axis, and always aims at a target Object3D at world
// [0, targetHeight, 0]. Extracted out of Scene.jsx so LIGHTING — SPOTLIGHT can
// mount up to 3 of these (the panel's "+ ADD SPOT") without tripling the
// ref/shadow-map-resize/render logic.
//
// `tune` is one spotlight slice (liveTuneStore's `spotlight` / `spotlight2` /
// `spotlight3`). `lightGlobal`/`targetGlobal` are the FULL window property
// names (e.g. '__SPOTLIGHT__') this instance publishes itself under. Slot 1
// MUST be called with '__SPOTLIGHT__' / '__SPOTLIGHT_TARGET__' — those are
// locked by tests/e2e/ci/spotlight.spec.js and predate this component. Slots
// 2/3 use their own additive names (never a rename of the existing one, per
// CLAUDE.md's window.__* rule).
export default function SpotlightRig({ tune, lightGlobal, targetGlobal, helperColor = '#00e5ff' }) {
  const spotTarget = useMemo(() => new THREE.Object3D(), [])
  const spotRef = useRef(null)
  const prevMapSizeRef = useRef(null)

  // Published unconditionally (not gated on tune.enabled) — same rule as the
  // original Scene.jsx spotTarget: the target must never dangle, and a
  // disabled light still leaves its aim point inspectable.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    window[targetGlobal] = spotTarget
    return () => {
      if (window[targetGlobal] === spotTarget) delete window[targetGlobal]
    }
  }, [spotTarget, targetGlobal])

  // Shadow-map resolution/bias applied imperatively: resizing shadowMapSize
  // needs the old render target(s) disposed or the map goes stale/black.
  useEffect(() => {
    const light = spotRef.current
    if (!light) return
    const size = tune.shadowMapSize
    if (prevMapSizeRef.current !== null && prevMapSizeRef.current !== size) {
      light.shadow.mapSize.set(size, size)
      if (light.shadow.map) { light.shadow.map.dispose(); light.shadow.map = null }
      if (light.shadow.mapPass) { light.shadow.mapPass.dispose(); light.shadow.mapPass = null }
    }
    prevMapSizeRef.current = size
    light.shadow.bias = tune.shadowBias
  }, [tune.enabled, tune.shadowMapSize, tune.shadowBias])

  if (!tune.enabled) return null

  return (
    <>
      <group rotation={[0, THREE.MathUtils.degToRad(tune.yaw), 0]}>
        <spotLight
          ref={(node) => {
            spotRef.current = node
            if (typeof window !== 'undefined') window[lightGlobal] = node
          }}
          position={[0, tune.height, Math.max(0, tune.distance)]}
          target={spotTarget}
          color={tune.color}
          intensity={tune.intensity}
          angle={THREE.MathUtils.degToRad(tune.angle)}
          penumbra={tune.penumbra}
          decay={2}
          distance={tune.range}
          castShadow
          shadow-mapSize-width={tune.shadowMapSize}
          shadow-mapSize-height={tune.shadowMapSize}
          shadow-camera-near={0.5}
          shadow-camera-far={50}
          shadow-bias={tune.shadowBias}
          shadow-radius={tune.shadowRadius}
          shadow-blurSamples={tune.shadowBlurSamples}
        >
          {/* Cone gizmo — a SpotLightHelper attached to this light; updates
             each frame so it tracks the rig yaw + target aim live. */}
          {tune.gizmo && <Helper type={THREE.SpotLightHelper} args={[helperColor]} />}
        </spotLight>
      </group>
      <primitive object={spotTarget} position={[0, tune.targetHeight, 0]} />
    </>
  )
}
